import { Directive, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { fromEvent, map, Subject, switchMap, takeUntil, tap } from 'rxjs';

@Directive({
  standalone: true,
  selector: '[compareImages]',
})
export class CompareImagesDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.initDrag();
  }

  initDrag(): void {
    const elementWidth = this.elementRef.nativeElement.offsetWidth;
    const minX = (elementWidth * 10 - elementWidth * 50) / 100;
    const maxX = (elementWidth * 90 - elementWidth * 50) / 100;

    const imageContainer = this.elementRef.nativeElement
      .children[1] as HTMLElement;
    const handle = this.elementRef.nativeElement.children[2] as HTMLElement;

    const isTouchScreen = this.isTouchScreen();

    const startEvent = isTouchScreen ? 'touchstart' : 'mousedown';
    const moveEvent = isTouchScreen ? 'touchmove' : 'mousemove';
    const endEvent = isTouchScreen ? 'touchend' : 'mouseup';

    this.handleInteraction(
      elementWidth,
      minX,
      maxX,
      imageContainer,
      handle,
      startEvent,
      moveEvent,
      endEvent
    );
  }

  private isTouchScreen() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  private handleInteraction(
    elementWidth: number,
    minX: number,
    maxX: number,
    imageContainer: HTMLElement,
    handle: HTMLElement,
    startEvent: string,
    moveEvent: string,
    endEvent: string
  ) {
    const start$ = fromEvent<MouseEvent | TouchEvent>(handle, startEvent);
    const end$ = fromEvent<MouseEvent | TouchEvent>(document, endEvent);
    const move$ = fromEvent<MouseEvent | TouchEvent>(document, moveEvent).pipe(
      takeUntil(end$)
    );

    let initialX: number,
      currentX = 0;

    start$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((startEvent: MouseEvent | TouchEvent) => {
          const clientX = this.getClientX(startEvent);
          initialX = clientX - currentX;

          return move$.pipe(
            takeUntil(this.destroy$),
            map((moveEvent: MouseEvent | TouchEvent) => {
              const clientX = this.getClientX(moveEvent);
              currentX = clientX - initialX;
              return currentX;
            }),
            takeUntil(end$)
          );
        })
      )
      .subscribe((newX: number) => {
        currentX = newX;
        if (currentX >= minX && currentX <= maxX) {
          handle.style.transform = `translateX(${newX}px)`;
          imageContainer.style.width = `${elementWidth / 2 + currentX}px`;
        }
      });

    end$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      initialX = currentX;
    });
  }

  private getClientX(event: MouseEvent | TouchEvent): number {
    return event instanceof TouchEvent
      ? event.touches[0].clientX
      : event.clientX;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
